import { useRef, useState } from "react";
import { Download, ExternalLink, FileText, Lock, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/date-utils";
import {
  useDeleteVehicleDocument,
  useUpdateVehicleDocumentType,
  useUploadVehicleDocument,
  vehicleDocumentTypeLabels,
  vehicleDocumentUrl,
  type VehicleDocument,
  type VehicleDocumentType,
} from "@/hooks/useVehicles";

const ACCEPTED_TYPES =
  "application/pdf,image/jpeg,image/png,image/webp,image/heic";

function formatSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function DocumentTypeSelect({
  value,
  onChange,
  testId,
}: {
  value: VehicleDocumentType;
  onChange: (value: VehicleDocumentType) => void;
  testId?: string;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => onChange(next as VehicleDocumentType)}
    >
      <SelectTrigger className="w-52" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(vehicleDocumentTypeLabels).map(([type, label]) => (
          <SelectItem key={type} value={type}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function VehicleDocumentsTab({
  vehicleId,
  documents,
}: {
  vehicleId: string;
  documents: VehicleDocument[];
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] =
    useState<VehicleDocumentType>("registration");
  const upload = useUploadVehicleDocument();
  const remove = useDeleteVehicleDocument();
  const updateType = useUpdateVehicleDocumentType();

  const handleFiles = (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      upload.mutate({ vehicleId, file, documentType: uploadType });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" />
          Documents are private: only signed-in employees can open them.
        </p>
        <div className="flex items-end gap-2">
          <div className="space-y-2">
            <Label>Document type</Label>
            <DocumentTypeSelect
              value={uploadType}
              onChange={setUploadType}
              testId="vehicle-document-upload-type"
            />
          </div>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? "Uploading..." : "Upload"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            className="hidden"
            onChange={(event) => handleFiles(event.target.files)}
            data-testid="vehicle-document-input"
          />
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="rounded-lg border py-8 text-center text-muted-foreground">
          No documents yet. Upload e.g. the vehicle registration (PDF or photo).
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((document) => (
            <Card
              key={document.id}
              data-testid={`vehicle-document-${document.id}`}
            >
              <CardContent className="flex flex-col gap-3 p-3 md:flex-row md:items-center md:gap-4">
                <FileText className="hidden h-8 w-8 shrink-0 text-muted-foreground md:block" />
                <div className="min-w-0 flex-1">
                  <a
                    href={vehicleDocumentUrl(document.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate font-medium hover:underline"
                    data-testid={`vehicle-document-link-${document.id}`}
                  >
                    {document.fileName}
                  </a>
                  <p className="text-sm text-muted-foreground">
                    {formatSize(document.sizeBytes)}
                    {document.createdAt &&
                      ` · ${formatDate(new Date(document.createdAt))}`}
                    {document.uploadedBy &&
                      ` · ${document.uploadedBy.firstName} ${document.uploadedBy.lastName}`}
                  </p>
                </div>
                <DocumentTypeSelect
                  value={document.documentType}
                  onChange={(documentType) =>
                    updateType.mutate({ id: document.id, documentType })
                  }
                />
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" asChild title="Open">
                    <a
                      href={vehicleDocumentUrl(document.id)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button size="icon" variant="ghost" asChild title="Download">
                    <a href={vehicleDocumentUrl(document.id, true)}>
                      <Download className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Delete"
                    onClick={() => {
                      if (confirm(`Delete "${document.fileName}"?`)) {
                        remove.mutate(document.id);
                      }
                    }}
                    data-testid={`delete-vehicle-document-${document.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
